import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo } from 'react'

import type { Descendant } from 'slate'
import { Transforms, createEditor } from 'slate'
import { withHistory } from 'slate-history'
import type { RenderElementProps } from 'slate-react'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import type { RenderLeafProps } from 'slate-react/dist/components/editable'

import IconClose from '@/assets/icons/icon-close.svg'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context.tsx'
import { PlaceholderTextPreview } from '@/modules/message/components/placeholder/placeholder-text-preview.tsx'
import { detectVariant } from '@/modules/message/components/placeholder/variants'
import { classNames } from '@/utils/helpers.ts'
import { noop } from '@/utils/misc.ts'

import type {
  CustomText,
  ParagraphElement,
  PlaceholderElement,
} from './custom-types'
import './custom-types.d.ts'

export const useEditor = () => {
  return useMemo(() => {
    const editor = withReact(withHistory(createEditor()))
    const { isInline, isVoid, markableVoid } = editor

    editor.isInline = (element) =>
      element.type === 'placeholder' ? true : isInline(element)
    editor.isVoid = (element) =>
      element.type === 'placeholder' ? true : isVoid(element)
    editor.markableVoid = (element) =>
      element.type === 'placeholder' || markableVoid(element)

    return editor
  }, [])
}

export type PlaceholderEditor = ReturnType<typeof useEditor>

export interface PlaceholderEditorProps {
  editor: PlaceholderEditor
  initialValue?: Descendant[]
  placeholder?: string
  className?: string
}

export const PlaceholderEditor = (props: PlaceholderEditorProps) => {
  const { editor, placeholder, initialValue, className } = props
  const { updatePlaceholderState, onChange } = useMessageEditor()

  const renderElement = useCallback(
    (props: RenderElementProps) => (
      <Element
        {...props}
        onClose={(event: MouseEvent) => {
          event.preventDefault()
          event.stopPropagation()

          if (!event.target) {
            return
          }

          if (ReactEditor.hasTarget(editor, event.target)) {
            const pathRef = ReactEditor.findPath(editor, props.element)

            if (pathRef) {
              Transforms.removeNodes(editor, { at: pathRef })
              updatePlaceholderState(nodes.stringify(editor.children))
            }
          }
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor],
  )

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    [],
  )

  useEffect(
    () => {
      editor.children = initialValue ?? []
      onChange(editor.children)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, initialValue],
  )

  return (
    <Slate
      editor={editor}
      initialValue={initialValue ?? []}
      onChange={onChange}
    >
      <Editable
        className={classNames(className, 'outline-none')}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        data-testid="message-editor"
        scrollSelectionIntoView={noop}
        renderPlaceholder={({ children, attributes }) => (
          <div {...attributes}>
            <PlaceholderTextPreview text={String(children)} />
          </div>
        )}
      />
    </Slate>
  )
}

PlaceholderEditor.insert = (
  editor: PlaceholderEditor,
  character: { id: string; name: string },
) => {
  Transforms.insertNodes(
    editor,
    nodes.placeholder(character.id, character.name),
  )
  Transforms.insertNodes(editor, nodes.text(' '))
  Transforms.move(editor, { edge: 'focus' })
}

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  const leafNode = leaf as CustomText

  if (leafNode.bold) {
    children = <strong>{children}</strong>
  }

  if (leafNode.code) {
    children = <code>{children}</code>
  }

  if (leafNode.italic) {
    children = <em>{children}</em>
  }

  return <span {...attributes}>{children}</span>
}

const Element = (props: CloseProps & RenderElementProps) => {
  const { attributes, children, element } = props

  switch (element.type) {
    case 'placeholder':
      return <Placeholder {...props} />
    default:
      return <p {...attributes}>{children}</p>
  }
}

interface CloseProps {
  onClose?: (event: MouseEvent) => void
}

const Placeholder = ({
  attributes,
  children,
  element,
  onClose,
}: CloseProps & RenderElementProps) => {
  if (element.type != 'placeholder') {
    return
  }

  return (
    <span
      {...attributes}
      contentEditable={false}
      data-cy={`placeholder-${element.character.replace(' ', '-')}`}
      className="border-border-secondary bg-secondary-50 dark:bg-secondary-500 dark:text-secondary-100 inline-flex items-center gap-1 rounded-md border px-2 text-sm"
    >
      {element.character}
      <button
        type="button"
        className="inline-block"
        onClick={onClose}
      >
        <IconClose className="text-secondary-200 hover:text-secondary-300 dark:text-secondary-300" />
      </button>
      {children}
    </span>
  )
}

export const nodes = {
  placeholder: (id: string, character: string): Descendant => ({
    type: 'placeholder',
    id,
    children: [{ text: '' }],
    character,
  }),
  text: (text: string): Descendant => ({ text }),
  paragraph: (children: Descendant[]): Descendant => ({
    type: 'paragraph',
    children,
  }),
  parse: (text: string) => {
    const elementNodes: Descendant[] = []
    const regex = /%([a-zA-Z0-9_\- ]+)%/g

    for (const line of text.split('\n')) {
      const paragraph: Descendant[] = []
      let start = 0
      let match

      while ((match = regex.exec(line)) !== null) {
        if (match.index > start) {
          paragraph.push({ text: line.slice(start, match.index) })
        }
        const id = match[1]
        const variant = detectVariant(id) ?? {
          id,
          name: id,
          tooltip: 'Unknown variable',
        }

        paragraph.push(nodes.placeholder(id, variant.name))
        start = regex.lastIndex
      }
      if (start < line.length) {
        paragraph.push({ text: line.slice(start) })
      }

      if (paragraph.length === 0) {
        paragraph.push({ text: '' })
      }

      elementNodes.push(nodes.paragraph(paragraph))
    }

    return elementNodes
  },
  stringify: (nodes: Descendant[]) => {
    return nodes
      .map((node) => {
        const paragraph = node as ParagraphElement

        if (paragraph.type !== 'paragraph') {
          return ''
        }

        return paragraph.children
          .map((element) => {
            const placeholder = element as PlaceholderElement

            if (placeholder.type === 'placeholder') {
              return `%${placeholder.id}%`
            }
            const text = element as CustomText

            if (text.text) {
              return text.text
            }

            return undefined
          })
          .filter((text) => text !== undefined)
          .join('')
      })
      .join('\n')
  },
}
